import type { Polygon } from "geojson";
import type {
  DataSnapshot,
  DataSource,
  HazardEvent,
  SpotId,
  StatusObservation,
  ToiletFacility,
} from "./types";
import {
  DEFAULT_SPOT_ID,
  getSpotDefinition,
  SPOT_OPTIONS,
  SPOT_TOILETS,
} from "./spots";

export { DEFAULT_SPOT_ID, SPOT_OPTIONS };

export const ARIAKE_CENTER = getSpotDefinition("ariake").center;

export const TOILET_IDS = {
  rinkaiDisasterPark: "d7200001-0000-4000-8000-000000000003",
  symbolPromenade: "d7200001-0000-4000-8000-000000000004",
  tokyoBigSight: "d7200001-0000-4000-8000-000000000002",
  ariakeWestWharf: "d7200001-0000-4000-8000-000000000001",
  ariakePassengerTerminal: "d7200001-0000-4000-8000-000000000007",
  ariakeSportsCenter: "d7200001-0000-4000-8000-000000000008",
  ariakeTennisPark: "d7200001-0000-4000-8000-000000000005",
  ariakeStation: "d7200001-0000-4000-8000-000000000009",
} as const;

const weekly = (open: string, close: string) => [
  { days: [0, 1, 2, 3, 4, 5, 6], open, close },
];

export const sources: DataSource[] = [
  {
    id: "tokyo-barrier-free-wc",
    publisher: "東京都福祉局",
    datasetName: "公共施設のバリアフリートイレ情報",
    sourceUrl:
      "https://www.opendata.metro.tokyo.lg.jp/fukushi/R07/01/3_koukyoshisetsu_barieer_free_wc.csv",
    catalogUrl: "https://catalog.data.metro.tokyo.lg.jp/dataset/t000054d0000000342",
    license: "CC BY 4.0",
    attribution:
      "東京都福祉局「公共施設のバリアフリートイレ情報」（東京都オープンデータカタログ、CC BY 4.0）を加工して作成",
    sourceKind: "static_baseline",
    publisherUpdatedAt: "2025-01-01T00:00:00+09:00",
    fetchedAt: "2026-07-27T00:30:00+09:00",
    recordCount: 8936,
    health: "stale",
    staleAfterHours: 24 * 365,
    limitation:
      "トイレ室単位の設備データです。施設内に存在することは、一般利用できることを意味しません。",
    permissionStatus: "open",
  },
  {
    id: "koto-public-toilets",
    publisher: "江東区",
    datasetName: "公衆トイレ一覧",
    sourceUrl:
      "https://www.opendata.metro.tokyo.lg.jp/koto/131083_013_public_toilet.csv",
    catalogUrl: "https://catalog.data.metro.tokyo.lg.jp/dataset/t131083d0000000019",
    license: "CC BY 4.0",
    attribution:
      "江東区「公衆トイレ一覧」（東京都オープンデータカタログ、CC BY 4.0）を加工して作成",
    sourceKind: "static_baseline",
    publisherUpdatedAt: "2026-02-06T00:00:00+09:00",
    fetchedAt: "2026-07-27T00:30:00+09:00",
    recordCount: 192,
    health: "healthy",
    staleAfterHours: 24 * 30,
    limitation:
      "公開192件に有明住所の施設がないため、実証地域ではバリアフリーデータを補完利用しています。",
    permissionStatus: "open",
  },
  {
    id: "tokyo-railway-toilets",
    publisher: "東京都福祉局",
    datasetName: "都内鉄道駅のバリアフリートイレ情報",
    sourceUrl:
      "https://www.opendata.metro.tokyo.lg.jp/fukushi/R07/01/4_tonaitetsudoueki_barrier-free-wc.csv",
    catalogUrl: "https://catalog.data.metro.tokyo.lg.jp/dataset/t000054d0000000342",
    license: "CC BY 4.0",
    attribution:
      "東京都福祉局「都内鉄道駅のバリアフリートイレ情報」（東京都オープンデータカタログ、CC BY 4.0）を加工して作成",
    sourceKind: "static_baseline",
    publisherUpdatedAt: "2026-01-01T00:00:00+09:00",
    fetchedAt: "2026-07-28T00:00:00+09:00",
    recordCount: 32,
    health: "healthy",
    staleAfterHours: 24 * 365,
    limitation:
      "駅単位に集約しています。改札内外、運行時間、工事・閉鎖は駅係員または鉄道事業者へ確認してください。",
    permissionStatus: "open",
  },
  {
    id: "tokyo-daredemo-facilities",
    publisher: "東京都デジタルサービス局",
    datasetName: "だれでも東京（民間・観光関連施設）",
    sourceUrl:
      "https://www.opendata.metro.tokyo.lg.jp/digitalservice/130001_Daredemo_Tokyo_shopping.csv",
    catalogUrl: "https://catalog.data.metro.tokyo.lg.jp/dataset/t000029d0000000003",
    license: "CC BY 4.0",
    attribution:
      "東京都「だれでも東京（民間・観光関連施設）」（東京都オープンデータカタログ、CC BY 4.0）を加工して作成",
    sourceKind: "static_baseline",
    publisherUpdatedAt: "2024-12-06T00:00:00+09:00",
    fetchedAt: "2026-07-28T00:00:00+09:00",
    recordCount: 32,
    health: "stale",
    staleAfterHours: 24 * 365,
    limitation:
      "施設内設備の調査情報です。一般開放、営業時間、休館、購入・入場条件は施設へ確認してください。",
    permissionStatus: "open",
  },
  {
    id: "big-sight-events",
    publisher: "株式会社東京ビッグサイト",
    datasetName: "東京ビッグサイトのイベント情報",
    sourceUrl:
      "https://www.opendata.metro.tokyo.lg.jp/tokyobigsight/tokyobigsighteventinformation.csv",
    catalogUrl: "https://catalog.data.metro.tokyo.lg.jp/dataset/t001001d0000000001",
    license: "CC BY 4.0",
    attribution:
      "株式会社東京ビッグサイト「東京ビッグサイトのイベント情報」（東京都オープンデータカタログ、CC BY 4.0）を加工して作成",
    sourceKind: "official_plan",
    publisherUpdatedAt: "2026-06-25T00:00:00+09:00",
    fetchedAt: "2026-07-27T00:30:00+09:00",
    recordCount: 141,
    health: "stale",
    staleAfterHours: 24 * 35,
    limitation:
      "CSVは7〜9月版で、公式カレンダーより一版古い状態です。来場者数は含まれず、混雑は同時開催数等からの推定です。",
    permissionStatus: "open",
  },
  {
    id: "waterworks-manual",
    publisher: "東京都水道局",
    datasetName: "23区内の断水・濁水情報",
    sourceUrl:
      "https://www.waterworks.metro.tokyo.lg.jp/kurashi/menu_dakusui/dakusui?area=8",
    license: "東京都水道局サイトポリシー",
    attribution: "出典：東京都水道局（自動取込は許諾確認まで無効）",
    sourceKind: "manual_override",
    health: "permission_required",
    limitation:
      "公開API・オープンライセンスを確認できないため、自動転載せず公式ページへのリンクと手動確認だけに使用します。",
    permissionStatus: "required",
  },
  {
    id: "jma-earthquake",
    publisher: "気象庁",
    datasetName: "防災情報XML・地震火山高頻度フィード",
    sourceUrl: "https://www.data.jma.go.jp/developer/xml/feed/eqvol.xml",
    license: "気象庁ホームページ利用規約",
    attribution: "出典：気象庁ホームページを加工して作成",
    sourceKind: "official_observation",
    fetchedAt: "2026-07-27T00:30:00+09:00",
    health: "not_configured",
    staleAfterHours: 0.25,
    limitation:
      "配信の遅延・停止があり得ます。地震だけで閉鎖とは断定せず、江東区の観測震度に応じて要点検へ変更します。",
    permissionStatus: "open",
  },
  {
    id: "community-reports",
    publisher: "東京トイレマップ 利用者",
    datasetName: "匿名の現場報告",
    sourceUrl: "/site-information.html#privacy",
    license: "利用規約に基づく集計情報",
    attribution: "東京トイレマップ 匿名利用者報告",
    sourceKind: "community_observation",
    health: "healthy",
    staleAfterHours: 0.5,
    limitation: "単独報告では閉鎖と断定せず、短時間で失効します。",
    permissionStatus: "manual_only",
  },
  {
    id: "simulation",
    publisher: "東京トイレマップ",
    datasetName: "審査用訓練シナリオ",
    sourceUrl: "/site-information.html#policy",
    license: "MIT",
    attribution: "東京トイレマップ 訓練シナリオ",
    sourceKind: "simulation",
    fetchedAt: "2026-07-27T00:30:00+09:00",
    health: "healthy",
    limitation: "実際の災害・断水・混雑情報ではありません。",
    permissionStatus: "open",
  },
  {
    id: "toshima-public-toilets",
    publisher: "豊島区",
    datasetName: "公衆トイレ一覧",
    sourceUrl:
      "https://www.opendata.metro.tokyo.lg.jp/toyoshima/R4_public_toilet.csv",
    catalogUrl:
      "https://catalog.data.metro.tokyo.lg.jp/dataset/t131164d0000000009",
    license: "CC BY 4.0",
    attribution:
      "豊島区「公衆トイレ一覧」（東京都オープンデータカタログ、CC BY 4.0）を加工して作成",
    sourceKind: "static_baseline",
    publisherUpdatedAt: "2026-01-15T00:00:00+09:00",
    fetchedAt: "2026-07-28T00:00:00+09:00",
    recordCount: 6,
    health: "stale",
    staleAfterHours: 24 * 365,
    limitation:
      "公開された施設・設備情報です。現在の利用可否や夜間制限は現地または施設公式情報で確認してください。",
    permissionStatus: "open",
  },
];

export const toilets: ToiletFacility[] = [
  {
    id: TOILET_IDS.rinkaiDisasterPark,
    officialFacilityId: "tokyo-bf-13000-7-rinkai",
    name: "東京臨海広域防災公園",
    nameEn: "Tokyo Rinkai Disaster Prevention Park",
    address: "東京都江東区有明三丁目8番35号",
    coordinates: { longitude: 139.796527, latitude: 35.633692 },
    facilityType: "都立公園",
    operatorName: "国土交通省・東京都",
    publicAccess: "business_hours_only",
    openingLabel: "6:00〜20:00",
    openingPeriods: weekly("06:00", "20:00"),
    openingNote: "12月31日〜1月1日は閉園",
    features: {
      wheelchair: true,
      ostomate: true,
      baby_changing: true,
      child_seat: true,
      gender_neutral: true,
    },
    roomCount: 1,
    sourceIds: ["tokyo-barrier-free-wc"],
    sourceUpdatedAt: "2025-01-01T00:00:00+09:00",
  },
  {
    id: TOILET_IDS.symbolPromenade,
    officialFacilityId: "tokyo-bf-13000-7-symbol-promenade",
    name: "シンボルプロムナード公園",
    nameEn: "Symbol Promenade Park",
    address: "東京都江東区有明二丁目・三丁目",
    coordinates: { longitude: 139.789448, latitude: 35.632049 },
    facilityType: "都立公園",
    operatorName: "東京都港湾局",
    publicAccess: "always_public",
    openingLabel: "24時間",
    openingPeriods: weekly("00:00", "23:59"),
    features: {
      wheelchair: true,
      ostomate: false,
      baby_changing: true,
      child_seat: true,
      gender_neutral: true,
    },
    roomCount: 5,
    sourceIds: ["tokyo-barrier-free-wc"],
    sourceUpdatedAt: "2025-01-01T00:00:00+09:00",
  },
  {
    id: TOILET_IDS.tokyoBigSight,
    officialFacilityId: "tokyo-bf-13000-7-tokyo-big-sight",
    name: "東京ビッグサイト",
    nameEn: "Tokyo Big Sight",
    address: "東京都江東区有明三丁目10番1号",
    coordinates: { longitude: 139.798662, latitude: 35.633601 },
    facilityType: "イベント施設",
    operatorName: "株式会社東京ビッグサイト",
    publicAccess: "event_only",
    openingLabel: "8:00〜22:00（催事・入館条件による）",
    openingPeriods: weekly("08:00", "22:00"),
    openingNote: "施設内トイレのため、催事・入館条件を現地で確認してください",
    features: {
      wheelchair: true,
      ostomate: true,
      baby_changing: true,
      child_seat: true,
      gender_neutral: true,
    },
    roomCount: 20,
    sourceIds: ["tokyo-barrier-free-wc", "big-sight-events"],
    sourceUpdatedAt: "2025-01-01T00:00:00+09:00",
  },
  {
    id: TOILET_IDS.ariakeWestWharf,
    officialFacilityId: "tokyo-bf-13000-7-ariake-west-wharf",
    name: "有明西ふ頭公園",
    nameEn: "Ariake West Wharf Park",
    address: "東京都江東区有明三丁目",
    coordinates: { longitude: 139.793715, latitude: 35.628209 },
    facilityType: "都立公園",
    operatorName: "東京都港湾局",
    publicAccess: "always_public",
    openingLabel: "24時間",
    openingPeriods: weekly("00:00", "23:59"),
    features: {
      wheelchair: true,
      ostomate: false,
      baby_changing: false,
      child_seat: false,
      gender_neutral: true,
    },
    roomCount: 1,
    sourceIds: ["tokyo-barrier-free-wc"],
    sourceUpdatedAt: "2025-01-01T00:00:00+09:00",
  },
  {
    id: TOILET_IDS.ariakePassengerTerminal,
    officialFacilityId: "tokyo-bf-13000-7-ariake-terminal",
    name: "有明客船ターミナル",
    nameEn: "Ariake Passenger Terminal",
    address: "東京都江東区有明三丁目12番13号",
    coordinates: { longitude: 139.79248, latitude: 35.629517 },
    facilityType: "港湾施設",
    operatorName: "東京都港湾局",
    publicAccess: "business_hours_only",
    openingLabel: "9:00〜17:00",
    openingPeriods: weekly("09:00", "17:00"),
    features: {
      wheelchair: true,
      ostomate: false,
      baby_changing: true,
      child_seat: true,
      gender_neutral: true,
    },
    roomCount: 3,
    sourceIds: ["tokyo-barrier-free-wc"],
    sourceUpdatedAt: "2025-01-01T00:00:00+09:00",
  },
  {
    id: TOILET_IDS.ariakeSportsCenter,
    officialFacilityId: "tokyo-bf-13000-7-ariake-sports-center",
    name: "有明スポーツセンター",
    nameEn: "Ariake Sports Center",
    address: "東京都江東区有明二丁目3番5号",
    coordinates: { longitude: 139.783486, latitude: 35.633693 },
    facilityType: "公共スポーツ施設",
    operatorName: "江東区",
    publicAccess: "business_hours_only",
    openingLabel: "8:30〜22:00",
    openingPeriods: weekly("08:30", "22:00"),
    openingNote: "休館日・利用条件は施設案内を確認してください",
    features: {
      wheelchair: true,
      ostomate: true,
      baby_changing: false,
      child_seat: true,
      gender_neutral: true,
    },
    roomCount: 5,
    sourceIds: ["tokyo-barrier-free-wc"],
    sourceUpdatedAt: "2025-01-01T00:00:00+09:00",
  },
  {
    id: TOILET_IDS.ariakeTennisPark,
    officialFacilityId: "tokyo-bf-13000-7-ariake-tennis-park",
    name: "有明テニスの森公園",
    nameEn: "Ariake Tennis Park",
    address: "東京都江東区有明二丁目2番22号",
    coordinates: { longitude: 139.787578, latitude: 35.635494 },
    facilityType: "都立スポーツ施設",
    operatorName: "東京都",
    publicAccess: "business_hours_only",
    openingLabel: "8:30〜21:30",
    openingPeriods: weekly("08:30", "21:30"),
    openingNote: "一部はイベント開催時のみ利用可能",
    features: {
      wheelchair: true,
      ostomate: true,
      baby_changing: true,
      child_seat: true,
      gender_neutral: true,
    },
    roomCount: 50,
    sourceIds: ["tokyo-barrier-free-wc"],
    sourceUpdatedAt: "2025-01-01T00:00:00+09:00",
  },
  {
    id: TOILET_IDS.ariakeStation,
    officialFacilityId: "tokyo-rail-17-12",
    name: "ゆりかもめ 有明駅",
    nameEn: "Yurikamome Ariake Station",
    address: "東京都江東区有明3丁目7番5号先",
    coordinates: { longitude: 139.793345, latitude: 35.634582 },
    facilityType: "鉄道駅",
    operatorName: "ゆりかもめ",
    publicAccess: "facility_users_only",
    openingLabel: "始発〜終車（詳細は駅で確認）",
    openingPeriods: [],
    openingNote: "改札内外・利用時間は駅で確認してください。",
    features: {
      wheelchair: true,
      ostomate: true,
      baby_changing: true,
      child_seat: true,
      gender_neutral: true,
    },
    roomCount: 3,
    sourceIds: ["tokyo-railway-toilets"],
    sourceUpdatedAt: "2022-11-01T00:00:00+09:00",
  },
];

const polygon = (
  west: number,
  south: number,
  east: number,
  north: number,
): Polygon => ({
  type: "Polygon",
  coordinates: [
    [
      [west, south],
      [east, south],
      [east, north],
      [west, north],
      [west, south],
    ],
  ],
});

const createHazards = (): HazardEvent[] => [
  {
    id: "event-crowding-20260729",
    type: "event_crowding",
    effect: "high_demand",
    title: "東京ビッグサイト周辺 イベント混雑見込み",
    description:
      "7月29日は複数催事が予定されています。同時開催数と利用ホールから、終了前後の相対的な需要増を推定しています。",
    validFrom: "2026-07-29T15:30:00+09:00",
    validTo: "2026-07-29T18:00:00+09:00",
    sourceId: "big-sight-events",
    sourceUpdatedAt: "2026-06-25T00:00:00+09:00",
    isSimulation: true,
    scenarios: ["event"],
    affectedToiletIds: [
      TOILET_IDS.tokyoBigSight,
      TOILET_IDS.rinkaiDisasterPark,
      TOILET_IDS.ariakePassengerTerminal,
    ],
    geometry: polygon(139.7948, 35.6293, 139.8015, 35.6365),
    limitation:
      "来場者数・便器別待ち時間は公開されていないため、正確な行列時間ではなく相対的な混雑見込みです。",
  },
  {
    id: "water-outage-drill",
    type: "water_outage",
    effect: "likely_unavailable",
    title: "有明三丁目 断水影響訓練",
    description:
      "給水設備の影響を想定した訓練です。受水槽を持つ施設では利用できる可能性があるため、閉鎖とは断定しません。",
    validFrom: "2026-07-29T13:00:00+09:00",
    validTo: "2026-07-29T16:00:00+09:00",
    sourceId: "simulation",
    sourceUpdatedAt: "2026-07-27T00:30:00+09:00",
    isSimulation: true,
    scenarios: ["outage"],
    affectedToiletIds: [
      TOILET_IDS.tokyoBigSight,
      TOILET_IDS.rinkaiDisasterPark,
      TOILET_IDS.ariakePassengerTerminal,
    ],
    geometry: polygon(139.7915, 35.6275, 139.802, 35.6365),
    limitation: "実際の断水情報ではありません。施設の受水槽・排水設備は未確認です。",
  },
  {
    id: "earthquake-inspection-drill",
    type: "earthquake_inspection",
    effect: "inspection_required",
    title: "江東区 震度5強想定・設備点検訓練",
    description:
      "強い揺れの後に給排水設備の点検が必要な状況を想定します。地震だけで利用不可とは判定しません。",
    validFrom: "2026-07-29T14:00:00+09:00",
    validTo: "2026-07-29T18:00:00+09:00",
    sourceId: "simulation",
    sourceUpdatedAt: "2026-07-27T00:30:00+09:00",
    isSimulation: true,
    scenarios: ["earthquake"],
    affectedToiletIds: toilets.map((toilet) => toilet.id),
    geometry: polygon(139.779, 35.626, 139.803, 35.644),
    limitation: "実際の地震情報ではありません。点検完了・現場確認を優先します。",
  },
];

const relativeIso = (now: Date, minutesAgo: number) =>
  new Date(now.getTime() - minutesAgo * 60_000).toISOString();

const createObservations = (now: Date): StatusObservation[] => [
  {
    id: "demo-observation-1",
    toiletId: TOILET_IDS.symbolPromenade,
    type: "community",
    status: "available",
    observedAt: relativeIso(now, 8),
    validUntil: new Date(now.getTime() + 22 * 60_000).toISOString(),
    sourceId: "simulation",
  },
  {
    id: "demo-observation-2",
    toiletId: TOILET_IDS.symbolPromenade,
    type: "community",
    status: "available",
    observedAt: relativeIso(now, 12),
    validUntil: new Date(now.getTime() + 18 * 60_000).toISOString(),
    sourceId: "simulation",
  },
  {
    id: "demo-observation-3",
    toiletId: TOILET_IDS.rinkaiDisasterPark,
    type: "community",
    status: "available",
    observedAt: relativeIso(now, 6),
    validUntil: new Date(now.getTime() + 24 * 60_000).toISOString(),
    sourceId: "simulation",
  },
  {
    id: "demo-event-observation-1",
    toiletId: TOILET_IDS.symbolPromenade,
    type: "community",
    status: "available",
    observedAt: "2026-07-29T16:22:00+09:00",
    validUntil: "2026-07-29T16:52:00+09:00",
    sourceId: "simulation",
  },
  {
    id: "demo-event-observation-2",
    toiletId: TOILET_IDS.symbolPromenade,
    type: "community",
    status: "available",
    observedAt: "2026-07-29T16:18:00+09:00",
    validUntil: "2026-07-29T16:48:00+09:00",
    sourceId: "simulation",
  },
  {
    id: "demo-outage-observation-1",
    toiletId: TOILET_IDS.ariakeWestWharf,
    type: "community",
    status: "available",
    observedAt: "2026-07-29T13:22:00+09:00",
    validUntil: "2026-07-29T13:52:00+09:00",
    sourceId: "simulation",
  },
  {
    id: "demo-outage-observation-2",
    toiletId: TOILET_IDS.ariakeWestWharf,
    type: "community",
    status: "available",
    observedAt: "2026-07-29T13:18:00+09:00",
    validUntil: "2026-07-29T13:48:00+09:00",
    sourceId: "simulation",
  },
];

export function createDemoSnapshot(
  now = new Date(),
  spotId: SpotId = DEFAULT_SPOT_ID,
): DataSnapshot {
  const spot = getSpotDefinition(spotId);
  const spotToilets = spotId === "ariake" ? toilets : SPOT_TOILETS[spotId];
  const isAriake = spotId === "ariake";

  return {
    region: {
      spotId: spot.id,
      municipalityCode: spot.municipalityCode,
      name: spot.name,
      center: spot.center,
      radiusMeters: spot.radiusMeters,
    },
    snapshotAt: now.toISOString(),
    demoClock: {
      event: "2026-07-29T16:30:00+09:00",
      outage: "2026-07-29T13:30:00+09:00",
      earthquake: "2026-07-29T14:15:00+09:00",
    },
    toilets: spotToilets,
    hazards: isAriake ? createHazards() : [],
    observations: isAriake ? createObservations(now) : [],
    sources,
  };
}

export const initialSnapshot = createDemoSnapshot();
